import dayjs from "dayjs";

import { getCityHotelRates } from "./actions/hotel-rate-actions";
import { buildEstimate } from "./estimate";
import type { FareQuote, HotelRate, TripDates, UsdRange, Workspace } from "./types";
import { cityKey } from "./workspace";

/** One city's estimate inside an exploration (a date window or a candidate city). */
export interface ExploredCity {
  city: string;
  perPerson: UsdRange;
  flightUsd: number | null;
  /** Home cities with no fare, left out of the average. */
  missing: number;
  /** The cheapest fare among the groups, with its airline. */
  cheapestFare: { usd: number; airline: string | null; flightNumber: string | null } | null;
}

/**
 * Ranks cities for one set of dates by per-person cost, lowest first, from fares and hotel
 * rates already fetched.
 * @param workspace - The workspace; its destinations and dates are replaced.
 * @param cities - Cities to rank.
 * @param dates - Dates to price.
 * @param fares - Per-traveler fares from the search for these cities and dates.
 * @param hotels - Hotel rates keyed by city key.
 * @returns The cities with their estimate, cheapest first; cities with no priced flight last.
 * @throws Never.
 */
export const rankCities = (
  workspace: Workspace,
  cities: string[],
  dates: TripDates,
  fares: ReadonlyMap<string, FareQuote>,
  hotels: ReadonlyMap<string, HotelRate>
): ExploredCity[] => {
  const estimate = buildEstimate({ ...workspace, destinations: cities, dates }, fares, hotels);
  return estimate.cities
    .map((city) => ({
      city: city.city,
      perPerson: city.perPerson,
      flightUsd: city.flights.averageUsd,
      missing: city.flights.missing.length,
      cheapestFare:
        city.flights.routes
          .filter((route) => route.status === "priced" && route.usd !== null)
          .sort((a, b) => (a.usd ?? 0) - (b.usd ?? 0))
          .map((route) => ({
            usd: route.usd ?? 0,
            airline: route.flight?.airline ?? null,
            flightNumber: route.flight?.flightNumber ?? null,
          }))[0] ?? null,
    }))
    .sort((a, b) => {
      if ((a.flightUsd === null) !== (b.flightUsd === null)) return a.flightUsd === null ? 1 : -1;
      return a.perPerson.low - b.perPerson.low;
    });
};

/**
 * Venue hotel rates for cities not already loaded.
 * @param cities - Cities to look up.
 * @param known - Hotel rates keyed by city key.
 * @returns The merged hotel map.
 * @throws Never; a failed read leaves those cities on the hotel assumption.
 */
export async function withHotelRates(
  cities: string[],
  known: ReadonlyMap<string, HotelRate>
): Promise<Map<string, HotelRate>> {
  const merged = new Map(known);
  const missing = cities.filter((city) => !known.has(cityKey(city)) || known.get(cityKey(city))?.status === "loading");
  if (missing.length === 0) return merged;
  const result = await getCityHotelRates(missing.map((city) => ({ city }))).catch(() => null);
  for (const city of missing) {
    merged.set(cityKey(city), result?.success ? (result.data[city] ?? { status: "error" }) : { status: "error" });
  }
  return merged;
}

/**
 * Dates to use for exploring cities when the planner has none: a midweek trip of `nights`
 * starting on the first Tuesday three or more weeks out.
 * @param nights - Trip length.
 * @param today - Reference day (for tests).
 * @returns The dates.
 * @throws Never.
 */
export const placeholderDates = (nights: number, today = dayjs()): TripDates => {
  let start = today.add(21, "day");
  while (start.day() !== 2) start = start.add(1, "day");
  return { start: start.format("YYYY-MM-DD"), end: start.add(nights, "day").format("YYYY-MM-DD") };
};
